/**
 * Billable · No charge · Internal — or Project work · Internal — the choice
 * every place that logs time offers, in place of the old billable switch.
 * Which options appear depends on where the time is going (lib/billing):
 * a fixed-fee or retainer project never offers Billable / No charge, and time
 * with no project and no client can only be Internal.
 */

import type { CSSProperties } from "react";

import { KIND_HINT, KIND_LABEL, type EntryKind } from "@/lib/billing";

/**
 * The state behind a picker: what the user chose, or null to follow the
 * project's default. Use with fitKind() so a choice that stops being on offer
 * (the project changed) falls back to the new default.
 */
export type KindPick = EntryKind | null;

export function KindPicker({ value, choices, onChange, size = "md", fromProject }: {
  value: EntryKind;
  choices: EntryKind[];
  onChange: (kind: EntryKind) => void;
  /** "lg" for the phone's 44pt+ tap targets. */
  size?: "md" | "lg";
  /** Say that the current value came from the project, not a choice. */
  fromProject?: boolean;
}) {
  if (choices.length === 1) {
    return (
      <span title={KIND_HINT[value]} style={{ ...single, ...(size === "lg" ? { minHeight: 46, fontSize: 14 } : null) }}>
        {KIND_LABEL[value]}
      </span>
    );
  }
  return (
    <div role="radiogroup" aria-label="Type of time" style={{ display: "inline-flex", gap: 3, padding: 3, borderRadius: 999, background: "var(--tint)", flexWrap: "wrap" }}>
      {choices.map((k) => {
        const on = k === value;
        return (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={on}
            title={KIND_HINT[k] + (on && fromProject ? " (from the project)" : "")}
            onClick={() => onChange(k)}
            style={{
              padding: size === "lg" ? "10px 14px" : "5px 12px",
              minHeight: size === "lg" ? 40 : undefined,
              borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
              fontSize: size === "lg" ? 14 : 12.5,
              border: on ? "1px solid var(--accentLine)" : "1px solid transparent",
              background: on ? "var(--surface)" : "transparent",
              color: on ? "var(--accent)" : "var(--ink3)", fontWeight: on ? 500 : 400,
            }}
          >{KIND_LABEL[k]}</button>
        );
      })}
    </div>
  );
}

/** The compact form, a dropdown, for the header's quick-log bar. */
export function KindSelect({ value, choices, onChange, style }: {
  value: EntryKind;
  choices: EntryKind[];
  onChange: (kind: EntryKind) => void;
  style?: CSSProperties;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as EntryKind)}
      disabled={choices.length === 1}
      aria-label="Type of time"
      title={KIND_HINT[value]}
      style={style}
    >
      {choices.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
    </select>
  );
}

const single: CSSProperties = {
  display: "inline-flex", alignItems: "center", padding: "5px 12px", borderRadius: 999,
  fontSize: 12.5, color: "var(--ink3)", background: "var(--tint)", whiteSpace: "nowrap",
};
